import { Alert, Box, Button, Group, Paper, Stack, Text, Title } from '@mantine/core'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { FcGoogle } from 'react-icons/fc'
import { TbAlertCircle } from 'react-icons/tb'
import { CompassRose } from '@/frontend/components/marina/CompassRose'
import { DepthContour } from '@/frontend/components/marina/DepthContour'
import { OmbakPattern } from '@/frontend/components/marina/OmbakPattern'
import { ThemeToggle } from '@/frontend/components/ThemeToggle'
import { getDefaultRoute } from '@/frontend/hooks/useAuth'
import { signIn } from '@/frontend/lib/auth-client'

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): { error?: string } => {
    const error = typeof search.error === 'string' ? search.error : undefined
    return error ? { error } : {}
  },
  beforeLoad: async ({ context }) => {
    try {
      const data = await context.queryClient.ensureQueryData({
        queryKey: ['auth', 'session'],
        queryFn: () => fetch('/api/auth/session', { credentials: 'include' }).then((r) => r.json()),
      })
      if (data?.user) {
        throw redirect({ to: getDefaultRoute(data.user.role) })
      }
    } catch (e) {
      if (e instanceof Error) return
      throw e
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const { error: searchError } = Route.useSearch()

  const handleGoogle = async () => {
    await signIn.social({ provider: 'google', callbackURL: '/dashboard' })
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        padding: 24,
      }}
    >
      {/* Backdrop: depth-contour lines fading outward — bathymetric chart feel */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          color: 'var(--mantine-color-laut-7)',
        }}
      >
        <DepthContour size={900} opacity={0.13} rings={9} />
      </Box>

      {/* Ombak watermark at bottom edge */}
      <Box
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          color: 'var(--mantine-color-laut-7)',
          pointerEvents: 'none',
        }}
      >
        <OmbakPattern height={42} opacity={0.16} stroke={1.4} />
      </Box>

      {/* Theme toggle top-right */}
      <Box style={{ position: 'absolute', top: 20, right: 20, zIndex: 2 }}>
        <ThemeToggle />
      </Box>

      {/* Hero brand mark + tagline above the login card */}
      <Stack align="center" gap="md" style={{ position: 'relative', zIndex: 1, maxWidth: 460, width: '100%' }}>
        <Box
          style={{
            width: 80,
            height: 80,
            borderRadius: 24,
            background: 'linear-gradient(135deg, #1A9C98 0%, #0E7C7B 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 18px 40px -10px rgba(14, 124, 123, 0.45)',
          }}
        >
          <CompassRose size={56} color="white" stroke={1.4} />
        </Box>

        <Stack gap={6} align="center">
          <Title
            order={1}
            ta="center"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2rem, 4vw, 2.75rem)',
              fontWeight: 600,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}
          >
            JP MARINE
          </Title>
          <Text c="dimmed" size="sm" ta="center" style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic' }}>
            Sistem manajemen pelabuhan modern untuk pesisir Indonesia
          </Text>
        </Stack>

        <Paper p="xl" radius="lg" w="100%" style={{ backdropFilter: 'blur(8px)' }}>
          <Stack gap="md">
            <Stack gap={2}>
              <Text fw={600} size="sm">
                Selamat datang kembali
              </Text>
              <Text c="dimmed" size="xs">
                Masuk untuk melanjutkan ke pelabuhan Anda.
              </Text>
            </Stack>

            {searchError && (
              <Alert icon={<TbAlertCircle size={16} />} color="red" variant="light">
                Login gagal, coba lagi.
              </Alert>
            )}

            <Button onClick={handleGoogle} fullWidth size="md" variant="default" leftSection={<FcGoogle size={20} />}>
              Continue with Google
            </Button>

            <Text c="dimmed" size="xs" ta="center">
              Dengan masuk, Anda menyetujui Syarat & Ketentuan dan Kebijakan Privasi.
            </Text>
          </Stack>
        </Paper>

        <Group gap={6} mt="sm">
          <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.18em', fontWeight: 500 }}>
            Pelabuhan
          </Text>
          <Box w={4} h={4} bg="laut.5" style={{ borderRadius: '50%' }} />
          <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.18em', fontWeight: 500 }}>
            Bali · Indonesia
          </Text>
        </Group>
      </Stack>
    </Box>
  )
}
